import { useId, useLayoutEffect, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAppearance } from '@/features/appearance';
import { cn } from '@/lib/utils';

/** Keep controls mounted when reducing the form to its essential sections. */
export function FormSection({
  title,
  children,
  essential = false,
  invalid = false,
  className,
}: {
  title: string;
  children: ReactNode;
  essential?: boolean;
  invalid?: boolean;
  className?: string;
}) {
  const { variant } = useAppearance();
  const id = useId();
  const [expanded, setExpanded] = useState(essential);
  useLayoutEffect(() => {
    if (invalid) setExpanded(true);
  }, [invalid]);
  const collapsible = variant.progressiveForms === true;
  const open = !collapsible || expanded || invalid;
  return (
    <section className={cn('form-section min-w-0', className)}>
      <h3 className="text-sm font-semibold">
        <button
          type="button"
          hidden={!collapsible}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setExpanded((value) => !value)}
          className="flex min-h-11 w-full items-center justify-between gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {title}
          <ChevronDown className={cn('size-4 shrink-0', open && 'rotate-180')} aria-hidden="true" />
        </button>
        <span hidden={collapsible}>{title}</span>
      </h3>
      <div
        id={id}
        hidden={!open}
        inert={!open}
        className={open ? 'grid gap-4 pt-4 sm:grid-cols-2' : 'hidden'}
      >
        {children}
      </div>
    </section>
  );
}
